/**
 * assessmentAnswersLoader — hydration for `buildStructuredDetailPanel`'s
 * "Your answers" disclosure (project-docs/plans/library-assessment-detail-redesign.md,
 * Phase 5).
 *
 * `buildStructuredDetailPanel` wires this module's `onToggle` handler into
 * `wireDisclosures` by default, so ALL FOUR HOSTS (chat "View full results"
 * card, /library Assessments tab, /library Topics-tab clinical-insight
 * drill, and the renderer's own self-call) get hydration for free — no
 * per-host wiring. A host-supplied `onToggle` still fires on every toggle
 * (composed alongside this one in `buildStructuredDetailPanel`, not
 * replaced).
 *
 * State machine: idle -> loading -> loaded | error.
 *   - idle -> loading on the user's first expand (the disclosure starts
 *     collapsed, so nothing fetches until they open it).
 *   - error -> loading again when the user collapses and re-expands (retry).
 *   - loading -> no-op on further expands while the request is in flight
 *     (the in-flight promise is reused, never a duplicate fetch).
 *   - loaded -> no-op on every later expand (rows already rendered).
 * DOM writes are not gated on `panel.isConnected` since a write after
 * teardown just lands in a detached tree that is garbage-collected with the
 * panel — harmless. (Neither `right-panel:open` nor `right-panel:drill` hand
 * this renderer an `onDispose` hook, so there is no reliable teardown signal
 * to key on anyway.)
 *
 * @module chat/assessmentAnswersLoader
 */

import { API } from '../core/api.js';

/** The one disclosure key this loader ever acts on. */
const ANSWERS_KEY = 'assessment-answers';

const LOADING_TEXT = 'Loading your answers…';
const ERROR_TEXT = "We couldn't load your answers. Collapse and expand this section to retry.";
const EMPTY_TEXT = 'No answers to show yet.';

/**
 * Find the "Your answers" hydration target — `.insight-detail__answers-content`,
 * a child of the disclosure body scoped so writes never clobber the sibling
 * "Completed <date>" line the disclosure body also carries.
 * @param {HTMLElement} panel
 * @returns {HTMLElement|null}
 */
function getAnswersBody(panel) {
  const toggle = panel.querySelector(`[data-disclosure-toggle="${ANSWERS_KEY}"]`);
  return toggle ? toggle.nextElementSibling?.querySelector('.insight-detail__answers-content') ?? null : null;
}

/**
 * Replace the hydration target's content with a single node. Deliberately
 * NOT gated on `panel.isConnected` — a response can land before the host
 * appends the panel, and that pre-attach write is the normal path (see the
 * module header).
 * @param {HTMLElement} panel
 * @param {Node} node
 */
function setBodyContent(panel, node) {
  const body = getAnswersBody(panel);
  if (!body) return;
  body.innerHTML = '';
  body.appendChild(node);
}

/**
 * @param {string} text
 * @param {string} className
 * @returns {HTMLParagraphElement}
 */
function buildMessage(text, className) {
  const p = document.createElement('p');
  p.className = className;
  p.textContent = text;
  return p;
}

/**
 * A pair carries dot-strip position data worth rendering: integer index
 * within an integer count, on a scale small enough that dots stay readable
 * (the backend only stamps these on RATING_SCALE answers).
 * @param {Object} pair
 * @returns {boolean}
 */
function hasDotPosition(pair) {
  return Number.isInteger(pair?.optionIndex)
    && Number.isInteger(pair?.optionCount)
    && pair.optionCount > 1
    && pair.optionCount <= 10
    && pair.optionIndex >= 0
    && pair.optionIndex < pair.optionCount;
}

/**
 * Build the Q&A rows list — one line per answer: number rail → question →
 * position-of-N dot strip (only when the backend stamped `optionIndex`/
 * `optionCount`, i.e. RATING_SCALE) → the answer, right-aligned in the data
 * accent. Question/answer are user-authored free text (questionText is
 * content-registry copy; answerText is service-flattened from the user's own
 * stored response) — assigned with `textContent` only, never `innerHTML`, so
 * neither can execute as markup (the dot strip is generated nodes, no text).
 * This is the first `buildStructuredDetailPanel` consumer that bypasses the
 * renderMarkdown/DOMPurify path entirely.
 * @param {Array<{questionText: string, answerText: string, optionIndex?: number, optionCount?: number}>} pairs
 * @returns {HTMLElement}
 */
function buildAnswersList(pairs) {
  const list = document.createElement('div');
  list.className = 'insight-detail__answers-list';

  pairs.forEach((pair, i) => {
    const row = document.createElement('div');
    row.className = 'insight-detail__answer-row';

    const num = document.createElement('span');
    num.className = 'insight-detail__answer-num';
    num.textContent = String(i + 1);
    row.appendChild(num);

    const question = document.createElement('div');
    question.className = 'insight-detail__answer-question';
    question.textContent = pair?.questionText ?? '';
    row.appendChild(question);

    if (hasDotPosition(pair)) {
      const dots = document.createElement('span');
      dots.className = 'insight-detail__answer-dots';
      dots.setAttribute('aria-hidden', 'true');
      for (let d = 0; d < pair.optionCount; d++) {
        const dot = document.createElement('span');
        dot.className = d === pair.optionIndex
          ? 'insight-detail__answer-dot insight-detail__answer-dot--on'
          : 'insight-detail__answer-dot';
        dots.appendChild(dot);
      }
      row.appendChild(dots);
    }

    const answer = document.createElement('div');
    answer.className = 'insight-detail__answer-text';
    answer.textContent = pair?.answerText ?? '';
    row.appendChild(answer);

    list.appendChild(row);
  });

  return list;
}

/**
 * Create the `onToggle(key, expanded)` handler `buildStructuredDetailPanel`
 * wires for one panel instance. Captures `panel` + `questionnaireId` by
 * closure; the returned function is idempotent per the state machine above.
 *
 * @param {HTMLElement} panel - The panel root `wireDisclosures` was called on.
 * @param {string} questionnaireId - Content-registry questionnaire id.
 * @returns {(key: string, expanded: boolean) => (Promise<void>|undefined)}
 *   Returns the in-flight/settled promise when it starts or reuses a fetch
 *   (handy for tests to await); `undefined` for every no-op call.
 */
export function createAssessmentAnswersOnToggle(panel, questionnaireId) {
  /** @type {'idle'|'loading'|'loaded'|'error'} */
  let state = 'idle';
  let inFlight = null;

  function fetchAndRender() {
    state = 'loading';
    setBodyContent(panel, buildMessage(LOADING_TEXT, 'insight-detail__answers-placeholder'));

    inFlight = API.getFrameworkAnswers(questionnaireId)
      .then((result) => {
        const pairs = result?.pairs;
        if (!result || !Array.isArray(pairs)) {
          state = 'error';
          setBodyContent(panel, buildMessage(ERROR_TEXT, 'insight-detail__answers-error'));
          return;
        }
        state = 'loaded';
        setBodyContent(
          panel,
          pairs.length > 0
            ? buildAnswersList(pairs)
            : buildMessage(EMPTY_TEXT, 'insight-detail__answers-placeholder')
        );
      })
      .catch(() => {
        state = 'error';
        setBodyContent(panel, buildMessage(ERROR_TEXT, 'insight-detail__answers-error'));
      })
      .finally(() => {
        inFlight = null;
      });

    return inFlight;
  }

  return function assessmentAnswersOnToggle(key, expanded) {
    if (key !== ANSWERS_KEY || !expanded) return undefined;
    if (state === 'loading' || state === 'loaded') return inFlight || undefined;
    return fetchAndRender();
  };
}
